// Reusable uPlot React wrapper (owned by the web:charts agent).
//
// uPlot is imperative; this component owns its lifecycle: create on mount,
// setData when data changes, setSize on container resize, and destroy+recreate
// when the options identity changes (theme switch, series change). Width tracks
// the container via ResizeObserver so the chart is responsive; height comes
// from the options. A shared cursor sync key can be passed so multiple charts
// (rtt / loss / jitter over the same run) move their crosshair together.
//
// uPlot's stylesheet is injected programmatically (see UPLOT_CSS) rather than
// imported as a .css module, so the wrapper compiles under our strict tsconfig
// without depending on Vite's asset-module type declarations.

import { useEffect, useRef } from 'react';
import uPlot from 'uplot';

export interface UplotChartProps {
  /** uPlot options WITHOUT width (width is managed here). height is honoured. */
  options: Omit<uPlot.Options, 'width'> & { width?: number };
  data: uPlot.AlignedData;
  /** Called once after the instance is (re)created. */
  onCreate?: (u: uPlot) => void;
  className?: string;
  ariaLabel?: string;
}

/** Thin, responsive React wrapper around a single uPlot instance. */
export function UplotChart({ options, data, onCreate, className, ariaLabel }: UplotChartProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const uRef = useRef<uPlot | null>(null);
  const dataRef = useRef<uPlot.AlignedData>(data);
  dataRef.current = data;

  useEffect(() => {
    ensureUplotCss();
  }, []);

  // Create / recreate when options identity changes.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    ensureUplotCss();
    const width = host.clientWidth || 600;
    const opts = { ...options, width } as uPlot.Options;
    const u = new uPlot(opts, dataRef.current, host);
    uRef.current = u;
    onCreate?.(u);
    return () => {
      u.destroy();
      uRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Push new data without recreating.
  useEffect(() => {
    uRef.current?.setData(data);
  }, [data]);

  // Track container width.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const u = uRef.current;
      if (!u) return;
      const w = host.clientWidth;
      if (w > 0 && w !== u.width) u.setSize({ width: w, height: u.height });
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  return <div ref={hostRef} className={className} role="img" aria-label={ariaLabel} />;
}

/** Format a microsecond-since-start value as seconds for an axis label. */
export function fmtSeconds(us: number): string {
  return (us / 1e6).toFixed(us < 1e6 ? 2 : 1);
}

// uPlot's core stylesheet (v1.6.x), injected once. Colours here are only the
// cursor/select chrome; our options override strokes/fills per theme.
const UPLOT_CSS = `
.uplot, .uplot *, .uplot *::before, .uplot *::after { box-sizing: border-box; }
.uplot { font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; line-height: 1.5; width: max-content; }
.u-title { text-align: center; font-size: 18px; font-weight: bold; }
.u-wrap { position: relative; user-select: none; }
.u-over, .u-under { position: absolute; }
.u-under { overflow: hidden; }
.uplot canvas { display: block; position: relative; width: 100%; height: 100%; }
.u-axis { position: absolute; }
.u-legend { font-size: 12px; margin: auto; text-align: center; }
.u-inline { display: block; }
.u-inline * { display: inline-block; }
.u-inline tr { margin-right: 16px; }
.u-legend th { font-weight: 600; }
.u-legend th > * { vertical-align: middle; display: inline-block; }
.u-legend .u-marker { width: 1em; height: 1em; margin-right: 4px; background-clip: padding-box !important; }
.u-inline.u-live th::after { content: ":"; vertical-align: middle; }
.u-inline:not(.u-live) .u-value { display: none; }
.u-series > * { padding: 4px; }
.u-series th { cursor: pointer; }
.u-legend .u-off > * { opacity: 0.3; }
.u-select { background: rgba(0,0,0,0.07); position: absolute; pointer-events: none; }
.u-cursor-x, .u-cursor-y { position: absolute; left: 0; top: 0; pointer-events: none; will-change: transform; z-index: 100; }
.u-hz .u-cursor-x, .u-vt .u-cursor-y { height: 100%; border-right: 1px dashed #607D8B; }
.u-hz .u-cursor-y, .u-vt .u-cursor-x { width: 100%; border-bottom: 1px dashed #607D8B; }
.u-cursor-pt { position: absolute; top: 0; left: 0; border-radius: 50%; border: 0 solid; pointer-events: none; will-change: transform; z-index: 100; background-clip: padding-box !important; }
.u-axis.u-off, .u-select.u-off, .u-cursor-x.u-off, .u-cursor-y.u-off, .u-cursor-pt.u-off { display: none; }
`;

let cssInjected = false;
function ensureUplotCss(): void {
  if (cssInjected || typeof document === 'undefined') return;
  if (document.getElementById('plm-uplot-css')) {
    cssInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'plm-uplot-css';
  style.textContent = UPLOT_CSS;
  document.head.appendChild(style);
  cssInjected = true;
}
